import { useCallback, useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { setNotification } from "store/features/notificationSlice";

import {
  setInvoice,
  setPayments,
  setPaymentStatus,
  getIbanInvoiceByUid,
  getIbanInvoicePaymentsByUid,
  updateIbanInvoicePaymentStatus,
  IbanInvoicePayment,
} from "store/features/ibanInvoiceDetailsSlice";

import { IbanInvoicePaymentStatusEnum } from "api";

import { routes } from "routes/routes";
import { DetailsTabId } from "./InvoiceDetailsTabs";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

export const useInvoiceDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const { ibanInvoiceUid } = useParams<{ ibanInvoiceUid: string }>();

  const initTab = searchParams.get("tab");

  const defaultTab =
    initTab === DetailsTabId.Payments
      ? DetailsTabId.Payments
      : DetailsTabId.Details;

  const [visibleTab, setVisibleTab] = useState<DetailsTabId>(defaultTab);

  const [isInvoiceLoading, setIsInvoiceLoading] = useState(false);

  const globalLoading = useAppSelector((state) => state.loading.isLoading);
  const isLoading = isInvoiceLoading || globalLoading;

  const invoiceDetails = useAppSelector((state) => state.ibanInvoiceDetails);

  const invoice = invoiceDetails?.invoice || null;
  const payments = invoiceDetails?.payments;

  const pageCount = invoiceDetails?.pageCount || 0;
  const pageNumber = invoiceDetails?.pageNumber || 1;
  const pageSize = invoiceDetails?.pageSize || 10;

  const handleGoBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(routes.invoices, { replace: true });
    }
  };

  const onAfterUpdateSubmit = useCallback(async () => {
    if (ibanInvoiceUid) {
      await dispatch(getIbanInvoiceByUid(ibanInvoiceUid)).unwrap();
    }
  }, [dispatch, ibanInvoiceUid]);

  const onAfterDeleteSubmit = useCallback(async () => {
    handleGoBack();
    dispatch(
      setNotification({
        message: "Посилання успішно видалено",
        severity: "success",
      }),
    );
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!ibanInvoiceUid) {
      handleGoBack();
      return;
    }

    setIsInvoiceLoading(true);

    Promise.all([
      dispatch(getIbanInvoiceByUid(ibanInvoiceUid)).unwrap(),
      dispatch(
        getIbanInvoicePaymentsByUid({
          uid: ibanInvoiceUid,
          pageNumber: 1,
          pageSize,
        }),
      ).unwrap(),
    ])
      .then(([invoice, _]) => {
        if (invoice == null) {
          handleGoBack();
        }
      })
      .catch(() => {
        handleGoBack();
      })
      .finally(() => {
        setIsInvoiceLoading(false);
      });

    return () => {
      dispatch(setInvoice(null));
      dispatch(setPayments([]));
    };
  }, [ibanInvoiceUid, dispatch, navigate]);

  useEffect(() => {
    if (initTab) {
      setVisibleTab(initTab as DetailsTabId);
    }
  }, [initTab]);

  const onPageChange = useCallback(
    (page: number) => {
      setIsInvoiceLoading(true);
      dispatch(
        getIbanInvoicePaymentsByUid({
          uid: ibanInvoiceUid || "",
          pageNumber: page,
          pageSize,
        }),
      )
        .unwrap()
        .finally(() => {
          setIsInvoiceLoading(false);
        });
    },
    [dispatch, ibanInvoiceUid, pageSize],
  );

  const onPaymentStatusChange = useCallback(
    async (payment: IbanInvoicePayment, status: string) => {
      analytics().sendConversionEvent(
        AnalyticsEvents.invoice_details_payment_status_change,
        { status },
        true,
      );

      const paymentId = payment.id;

      const paymentStatus = status as IbanInvoicePaymentStatusEnum;

      try {
        dispatch(
          setPaymentStatus({
            id: paymentId,
            status,
          }),
        );

        await dispatch(
          updateIbanInvoicePaymentStatus({
            ibanInvoicePaymentUid: paymentId,
            ibanInvoicePaymentStatus: paymentStatus,
          }),
        ).unwrap();

        await dispatch(
          getIbanInvoicePaymentsByUid({
            uid: ibanInvoiceUid || "",
            pageNumber,
            pageSize,
          }),
        ).unwrap();
      } catch (error) {
        dispatch(
          setPaymentStatus({
            id: paymentId,
            status: payment.status,
          }),
        );

        dispatch(
          setNotification({
            severity: "error",
            message: "Помилка оновлення статусу платежу",
          }),
        );
      }
    },
    [dispatch, ibanInvoiceUid, pageNumber, pageSize],
  );

  return {
    invoice,
    payments,
    pageCount,
    pageNumber,
    pageSize,
    isLoading,
    visibleTab,
    setVisibleTab,
    handleGoBack,
    onPageChange,
    onPaymentStatusChange,
    onAfterUpdateSubmit,
    onAfterDeleteSubmit,
  };
};
